// PWA: registro do service worker, instalação por plataforma e Web Push (SPEC A3/A17/A18, DESIGN §11).
// Implementação web de `PlataformaPwa` (plataforma/tipos.ts); era compartilhado/lib/pwa.ts, mesmo código.
import { useEffect, useState, useSyncExternalStore } from "react";
import { registerSW } from "virtual:pwa-register";
import type {
  ContextoConviteAvisos,
  EstadoPermissao,
  FaseConviteAvisos,
  InscricaoPushNavegador,
  TipoAparelho,
} from "../tipos";

// ── Service worker e atualização ────────────────────────────────────────────
// O SW novo assume sozinho (skipWaiting + clientsClaim em sw.ts). Quando isso acontece com a página aberta,
// ela ainda roda o JS antigo: avisamos "Nova versão disponível — Atualizar" (AvisoAtualizacao.tsx).
// Abas e apps instalados que ficam abertos por dias procuram versão nova a cada 30 min e ao voltar ao app.

const INTERVALO_BUSCA_ATUALIZACAO_MS = 30 * 60_000;
let atualizacaoDisponivel = false;
const ouvintesAtualizacao = new Set<() => void>();

function marcarAtualizacaoDisponivel(): void {
  if (atualizacaoDisponivel) return;
  atualizacaoDisponivel = true;
  ouvintesAtualizacao.forEach((f) => f());
}

/** Registra o SW (chamado uma vez no main.tsx). Não faz nada em dev. */
export function registrarServiceWorker(): void {
  // Import dinâmico que falhou (chunk de uma versão que saiu do ar): também é "versão nova". O erro segue para
  // quem importou (sem preventDefault): nas telas lazy o ErroRota assume (recarrega uma vez, depois "Recarregar")
  // e o aviso não aparece por cima dele; fora delas (ex.: jsqr do leitor de QR no iPhone) fica o aviso
  // "Atualizar". A carga inicial quebrada é da guarda-carga.js (index.html).
  window.addEventListener("vite:preloadError", () => {
    window.setTimeout(() => {
      if (!document.querySelector("[data-erro-rota]")) marcarAtualizacaoDisponivel();
    }, 1_000);
  });
  if (!("serviceWorker" in navigator) || import.meta.env.DEV) return;
  // Sem controlador na carga = primeira visita: o SW assumir agora não é "versão nova".
  const tinhaControlador = navigator.serviceWorker.controller !== null;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (tinhaControlador) marcarAtualizacaoDisponivel();
  });
  registerSW({
    immediate: true,
    // SW antigo ainda esperando (instalado por uma versão sem skipWaiting): mostra o aviso também.
    onNeedRefresh: marcarAtualizacaoDisponivel,
    onRegisteredSW(_url, registro) {
      if (!registro) return;
      const buscar = () => void registro.update().catch(() => undefined);
      window.setInterval(buscar, INTERVALO_BUSCA_ATUALIZACAO_MS);
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") buscar();
      });
    },
  });
}

/** true quando há versão nova no ar; use com `aplicarAtualizacao()` ("Atualizar"). */
export function useAtualizacaoDisponivel(): boolean {
  return useSyncExternalStore(
    (f) => {
      ouvintesAtualizacao.add(f);
      return () => ouvintesAtualizacao.delete(f);
    },
    () => atualizacaoDisponivel,
  );
}

/** Recarrega na versão nova. Se houver SW esperando (instalado por versão antiga), manda ele assumir antes. */
export async function aplicarAtualizacao(): Promise<void> {
  const registro = await navigator.serviceWorker?.getRegistration().catch(() => undefined);
  const esperando = registro?.waiting;
  if (esperando) {
    await new Promise<void>((pronto) => {
      navigator.serviceWorker.addEventListener("controllerchange", () => pronto(), { once: true });
      window.setTimeout(pronto, 1_500);
      esperando.postMessage({ tipo: "PULAR_ESPERA" });
    });
  }
  window.location.reload();
}

// ── Plataforma e instalação ─────────────────────────────────────────────────

export function detectarPlataforma(ua: string = navigator.userAgent): TipoAparelho {
  const ios = /iPad|iPhone|iPod/.test(ua) || (ua.includes("Macintosh") && navigator.maxTouchPoints > 1);
  if (ios) return /CriOS|FxiOS|EdgiOS|OPiOS|GSA\//.test(ua) ? "ios-outro" : "ios-safari";
  if (/Android/i.test(ua)) return "android";
  return "desktop";
}

/** Plataforma no formato da API (`InscricaoPushCorpo.plataforma`). */
export function plataformaApi(): "ios" | "android" | "desktop" {
  const p = detectarPlataforma();
  return p === "ios-safari" || p === "ios-outro" ? "ios" : p;
}

/** Aberto como app instalado (tela inicial)? */
export function estaInstalado(): boolean {
  const nav = navigator as Navigator & { standalone?: boolean };
  return window.matchMedia("(display-mode: standalone)").matches || nav.standalone === true;
}

interface EventoPromptInstalacao extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let promptGuardado: EventoPromptInstalacao | null = null;
const ouvintesPrompt = new Set<() => void>();

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    promptGuardado = e as EventoPromptInstalacao;
    ouvintesPrompt.forEach((f) => f());
  });
  window.addEventListener("appinstalled", () => {
    promptGuardado = null;
    ouvintesPrompt.forEach((f) => f());
  });
}

/** Android/Chrome: `disponivel` quando o navegador ofereceu instalar; `instalar()` abre o prompt nativo. */
export function usePromptInstalacao(): { disponivel: boolean; instalar: () => Promise<boolean> } {
  const disponivel = useSyncExternalStore(
    (f) => {
      ouvintesPrompt.add(f);
      return () => ouvintesPrompt.delete(f);
    },
    () => promptGuardado !== null,
  );
  return {
    disponivel,
    async instalar() {
      if (!promptGuardado) return false;
      await promptGuardado.prompt();
      const { outcome } = await promptGuardado.userChoice;
      promptGuardado = null;
      ouvintesPrompt.forEach((f) => f());
      return outcome === "accepted";
    },
  };
}

// ── Id estável do aparelho (inscrições push) ────────────────────────────────

const CHAVE_ID_CLIENTE = "gs.idCliente";

export function idCliente(): string {
  let id = localStorage.getItem(CHAVE_ID_CLIENTE);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(CHAVE_ID_CLIENTE, id);
  }
  return id;
}

// ── Web Push ────────────────────────────────────────────────────────────────

export function suportaPush(): boolean {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export function permissaoNotificacoes(): EstadoPermissao {
  return suportaPush() ? Notification.permission : "sem-suporte";
}

/** Acompanha a permissão de notificações (atualiza ao voltar para a aba). */
export function usePermissaoNotificacoes(): EstadoPermissao {
  const [estado, setEstado] = useState<EstadoPermissao>(() => permissaoNotificacoes());
  useEffect(() => {
    const atualizar = () => setEstado(permissaoNotificacoes());
    document.addEventListener("visibilitychange", atualizar);
    return () => document.removeEventListener("visibilitychange", atualizar);
  }, []);
  return estado;
}

function base64UrlParaBytes(base64Url: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64Url.length % 4)) % 4);
  const base64 = (base64Url + padding).replace(/-/g, "+").replace(/_/g, "/");
  const bruto = atob(base64);
  const bytes = new Uint8Array(new ArrayBuffer(bruto.length));
  for (let i = 0; i < bruto.length; i++) bytes[i] = bruto.charCodeAt(i);
  return bytes;
}

/**
 * Pede permissão (se preciso) e inscreve o aparelho no push com a chave VAPID da API
 * (`GET /api/publico/push/chave`). Devolve null se o usuário negar ou não houver suporte.
 * O chamador envia o resultado para `POST /api/eu/push/inscricoes` com `idCliente()` e `plataformaApi()`.
 */
export async function inscreverPush(vapidPublicKey: string): Promise<InscricaoPushNavegador | null> {
  if (!suportaPush()) return null;
  const permissao =
    Notification.permission === "default" ? await Notification.requestPermission() : Notification.permission;
  if (permissao !== "granted") return null;
  const registro = await navigator.serviceWorker.ready;
  const existente = await registro.pushManager.getSubscription();
  const inscricao =
    existente ??
    (await registro.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: base64UrlParaBytes(vapidPublicKey),
    }));
  const json = inscricao.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) return null;
  return { endpoint: json.endpoint, keys: { p256dh: json.keys.p256dh, auth: json.keys.auth } };
}

/** Remove a inscrição deste aparelho no navegador (a API é avisada à parte). */
export async function cancelarPushNoNavegador(): Promise<void> {
  if (!suportaPush()) return;
  const registro = await navigator.serviceWorker.ready;
  await (await registro.pushManager.getSubscription())?.unsubscribe();
}

/** Só o app tem inscrição de push própria do aparelho (contrato; o ponto de chamada fica atrás de `__ALVO__`). */
export async function desligarAvisosDoAparelho(): Promise<void> {}

// ── Só no app (contrato; os pontos de chamada ficam atrás de `__ALVO__` e nada disto entra no bundle web) ────

/** O convite do Início para ativar os avisos é do app da loja; no site continua a faixa "Instalar". */
export function useConviteAvisos(_contexto: ContextoConviteAvisos): FaseConviteAvisos | null {
  return null;
}

export function recusarConviteAvisos(): void {}

export function podeAbrirAjustesDoApp(): boolean {
  return false;
}

export async function abrirAjustesDoApp(): Promise<boolean> {
  return false;
}
